import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'

export function useDataFeedConfigMutations({
	organizationId,
	adapterType,
	existingConfigId,
	label,
	onClose,
}: {
	organizationId: string
	adapterType: 'sftp' | 'ukg_raas'
	existingConfigId: string | undefined
	label: string
	onClose: () => void
}) {
	const queryClient = useQueryClient()
	const { showToast } = useToast()

	const { data: runs } = useQuery({
		...trpc.admin.integrations.dataFeed.getRuns.queryOptions({
			configId: existingConfigId ?? '',
			limit: 10,
		}),
		enabled: !!existingConfigId,
	})

	const invalidateQueries = () => {
		queryClient.invalidateQueries({
			queryKey: trpc.admin.integrations.dataFeed.getByOrganization.queryKey({
				organizationId,
				adapterType,
			}),
		})
		queryClient.invalidateQueries({
			queryKey: trpc.admin.users.getPopulationLockStatus.queryKey({
				organizationId,
			}),
		})
	}

	const createMutation = useMutation({
		...trpc.admin.integrations.dataFeed.create.mutationOptions({
			onSuccess: () => {
				invalidateQueries()
				showToast(`${label} configuration created successfully`, 'info')
				onClose()
			},
			onError: (error) => {
				showToast(`Error creating configuration: ${error.message}`, 'error')
			},
		}),
	})

	const updateMutation = useMutation({
		...trpc.admin.integrations.dataFeed.update.mutationOptions({
			onSuccess: () => {
				invalidateQueries()
				showToast(`${label} configuration updated successfully`, 'info')
				onClose()
			},
			onError: (error) => {
				showToast(`Error updating configuration: ${error.message}`, 'error')
			},
		}),
	})

	const deleteMutation = useMutation({
		...trpc.admin.integrations.dataFeed.delete.mutationOptions({
			onSuccess: () => {
				invalidateQueries()
				showToast(`${label} configuration deleted successfully`, 'info')
				onClose()
			},
			onError: (error) => {
				showToast(`Error deleting configuration: ${error.message}`, 'error')
			},
		}),
	})

	return { runs, createMutation, updateMutation, deleteMutation }
}
